"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Loader2, Check, AlertTriangle, Zap } from "lucide-react";

export default function RushHourSpinnerScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("12:30 PM · the rush · illustration", "12:30 PM · la hora pico · ilustración")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Built to ", "Hecho para ")}
            <span className="text-redy-charcoal/30">{t("keep up.", "ir a tu ritmo.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "A slow POS turns the rush into a line out the door. Redy is built for the rush — fewer taps, multi-cart, held orders — so the screen isn't what slows your line.",
              "Un POS lento convierte la hora pico en una fila hasta la calle. Redy está hecho para la hora pico — menos taps, multi-cart, órdenes en espera — para que la pantalla no sea lo que frena tu fila."
            )}
          </p>
        </motion.div>

        {/* Side-by-side comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-[1080px] mx-auto">
          {/* Slow-POS side — generic illustration, no real vendor */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-[#FAFAF8] rounded-[28px] border border-black/5 p-6 md:p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="text-[11px] font-black tracking-widest uppercase text-redy-secondary/60">
                {t("A slow POS · 12:34 PM", "Un POS lento · 12:34 PM")}
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: "rgba(220,38,38,0.10)", color: "#DC2626" }}>
                <AlertTriangle className="w-2.5 h-2.5" strokeWidth={3} />
                {t("Lagging", "Trabado")}
              </div>
            </div>

            {/* "iPad" with spinner */}
            <div className="bg-redy-charcoal rounded-[20px] p-5 md:p-8 mb-5 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at center, rgba(220,38,38,0.30) 0%, transparent 65%)" }}
              />
              <div className="relative flex flex-col items-center justify-center min-h-[180px]">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                  className="mb-4"
                >
                  <Loader2 className="w-10 h-10" style={{ color: "#DC2626" }} strokeWidth={2.5} />
                </motion.div>
                <div className="text-[14px] font-bold text-white mb-1">
                  {t("Loading order…", "Cargando orden…")}
                </div>
                <div className="text-[11px] font-medium text-white/50">
                  {t("Sync with cloud · 4.2s", "Sincronizando · 4.2s")}
                </div>
              </div>
            </div>

            {/* Customer-facing complaint */}
            <div className="bg-white rounded-[16px] border border-black/5 p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-redy-secondary/60 mb-1">
                {t("Customer", "Cliente")}
              </div>
              <div className="text-[14px] font-bold text-redy-charcoal leading-snug">
                {t(
                  "\"Are you still trying to ring me up?\"",
                  "\"¿Sigues intentando cobrarme?\""
                )}
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#DC2626" }}>
                <AlertTriangle className="w-3 h-3" strokeWidth={3} />
                <span>{t("Line of 12. Cashier sweating.", "Fila de 12. El cajero sudando.")}</span>
              </div>
            </div>
          </motion.div>

          {/* Redy side — instant */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-white rounded-[28px] border border-black/5 p-6 md:p-8 relative overflow-hidden shadow-[0_30px_70px_rgba(0,0,0,0.06)]"
          >
            <div
              className="absolute -top-12 -right-12 w-[260px] h-[260px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(244,114,43,0.15) 0%, transparent 70%)" }}
            />

            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div className="text-[11px] font-black tracking-widest uppercase" style={{ color: "#D7263D" }}>
                  {t("Redy · 12:34 PM", "Redy · 12:34 PM")}
                </div>
                <div
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white"
                  style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
                >
                  <Zap className="w-2.5 h-2.5" strokeWidth={3} />
                  {t("Crisp", "Fluido")}
                </div>
              </div>

              {/* iPad with paid order */}
              <div className="bg-[#FAFAF8] rounded-[20px] p-5 md:p-8 mb-5 border border-black/5">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-[11px] font-black tracking-widest uppercase text-redy-secondary/60">
                    #142
                  </div>
                  <div className="flex items-center gap-1.5">
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.4, delay: 0.6 }}
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "#2ECC71" }}
                    >
                      <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                    </motion.div>
                    <div className="text-[11px] font-black uppercase tracking-wider" style={{ color: "#2ECC71" }}>
                      {t("Paid", "Cobrado")}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 text-[13px]">
                  <div className="flex justify-between text-redy-charcoal">
                    <span>2× Birria taco</span>
                    <span className="tabular-nums font-bold">$18.00</span>
                  </div>
                  <div className="flex justify-between text-redy-charcoal">
                    <span>1× Agua fresca</span>
                    <span className="tabular-nums font-bold">$4.00</span>
                  </div>
                  <div className="flex justify-between text-redy-charcoal">
                    <span>1× Elote</span>
                    <span className="tabular-nums font-bold">$6.50</span>
                  </div>
                </div>

                <div className="h-px bg-black/5 my-3" />

                <div className="flex items-baseline justify-between">
                  <div className="text-[10px] font-black uppercase tracking-wider text-redy-secondary/60">
                    {t("Total · cash", "Total · cash")}
                  </div>
                  <div className="text-[22px] font-black tabular-nums text-redy-charcoal">
                    $28.50
                  </div>
                </div>
              </div>

              {/* Customer happy */}
              <div className="bg-[#FAFAF8] rounded-[16px] border border-black/5 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-redy-secondary/60 mb-1">
                  {t("Cashier", "Cajero")}
                </div>
                <div className="text-[14px] font-bold text-redy-charcoal leading-snug">
                  {t(
                    "\"Next.\"",
                    "\"Siguiente.\""
                  )}
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#D7263D" }}>
                  <Zap className="w-3 h-3" strokeWidth={3} />
                  <span>{t("Order taken. Line still moving.", "Orden tomada. La fila avanza.")}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-[14px] font-medium text-redy-secondary mt-12 max-w-[600px] mx-auto"
        >
          {t(
            "Illustration. The point: the screen shouldn't be what slows your line.",
            "Ilustración. La idea: la pantalla no debería ser lo que frena tu fila."
          )}
        </motion.p>
      </div>
    </section>
  );
}
